import { useState } from 'react';
import { PALETTE } from '../tokens';
import { useAuth } from '../context/AuthContext';
import { safetyApi } from '../api/safety';

/**
 * SafetyGatedView — grounding screen shown when coherence_status === SAFETY_GATED.
 *
 * Hides score, domain sub-scores, and insight entirely.
 * Shows a clean, calm screen with soft copy and tappable resource cards.
 * Does not explain why the score is hidden.
 *
 * THE EXIT. Until this control existed, nothing in the product could
 * lower `users.safety_flag` — the gate was a one-way door, and a false
 * positive meant a person lost their dashboard permanently. "I'm fine"
 * closes it immediately, no waiting period and no second party. It sits
 * BELOW the resources on purpose: the resources are the point of the
 * screen, and the way out should be findable without being the first
 * thing offered.
 *
 * `onCleared` lets the parent re-fetch rather than this component
 * reaching for a router — the gated view is chosen by the parent's
 * snapshot read, so the parent is what has to change its mind.
 */
export function SafetyGatedView({ onCleared }) {
  const { user, token } = useAuth();
  const [clearing, setClearing] = useState(false);
  const [failed, setFailed] = useState(false);

  const handleClear = async () => {
    if (!user?.id || !token || clearing) return;
    setClearing(true);
    setFailed(false);
    try {
      await safetyApi.confirm(user.id, token);
      onCleared?.();
    } catch {
      // Never strand the person on a spinner. The retry is the same
      // tap; nothing about the failure is theirs to interpret.
      setFailed(true);
      setClearing(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '70vh',
        padding: '48px 28px',
        textAlign: 'center',
      }}
    >
      <h2
        className="serif"
        style={{
          fontSize: 26,
          fontWeight: 400,
          fontStyle: 'italic',
          color: PALETTE.text,
          lineHeight: 1.35,
          marginBottom: 16,
          maxWidth: 300,
        }}
      >
        Take a breath.{'\n'}You don't have to figure everything out right now.
      </h2>

      <p
        style={{
          fontSize: 14,
          color: PALETTE.textMid,
          lineHeight: 1.72,
          fontWeight: 300,
          maxWidth: 280,
          marginBottom: 36,
        }}
      >
        If you're going through something difficult, support is available.
      </p>

      {/* Explanation — transparent about what happened */}
      <p
        style={{
          fontSize: 13,
          color: PALETTE.textMid,
          lineHeight: 1.72,
          fontWeight: 300,
          maxWidth: 300,
          marginBottom: 28,
          fontStyle: 'italic',
        }}
      >
        We noticed your signal dropped into a range where we want to slow things down. Your scores are safe — we're just pausing the analysis for now.
      </p>

      {/* Subtle divider */}
      <div
        style={{
          width: 48,
          height: 1,
          background: PALETTE.border,
          marginBottom: 32,
        }}
      />

      {/* Resources — tappable cards */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          width: '100%',
          maxWidth: 300,
          marginBottom: 36,
        }}
      >
        <ResourceCard
          label="Talk to someone"
          detail="988 Suicide and Crisis Lifeline"
          action="Call or text 988"
          href="tel:988"
        />
        <ResourceCard
          label="Text support"
          detail="Crisis Text Line"
          action="Text HOME to 741741"
          href="sms:741741&body=HOME"
        />
      </div>

      {/* Soft CTA */}
      <p
        style={{
          fontSize: 13,
          color: PALETTE.textFaint,
          fontWeight: 300,
          fontStyle: 'italic',
          maxWidth: 260,
          lineHeight: 1.6,
          marginBottom: 28,
        }}
      >
        Come back to check in when you're ready.
      </p>

      {/* The way out. Quiet by design — a button styled to persuade
          would be pressure, and this is the one control on the screen
          that must not apply any. */}
      <button
        type="button"
        onClick={handleClear}
        disabled={clearing}
        aria-label="I'm fine — take me back"
        style={{
          background: 'none',
          border: 'none',
          padding: '8px 4px',
          fontSize: 13,
          fontWeight: 400,
          color: PALETTE.textMid,
          textDecoration: 'underline',
          textUnderlineOffset: 4,
          cursor: clearing ? 'default' : 'pointer',
          opacity: clearing ? 0.5 : 1,
          fontFamily: 'inherit',
        }}
      >
        {clearing ? 'One moment…' : "I'm fine — take me back"}
      </button>

      {failed && (
        <p
          role="alert"
          style={{
            fontSize: 12.5,
            color: PALETTE.textMid,
            fontWeight: 300,
            maxWidth: 260,
            lineHeight: 1.6,
            marginTop: 12,
          }}
        >
          That didn't go through. Tap again when you're ready.
        </p>
      )}
    </div>
  );
}

function ResourceCard({ label, detail, action, href }) {
  return (
    <a
      href={href}
      style={{
        display: 'block',
        textDecoration: 'none',
        background: PALETTE.surface,
        border: `1px solid ${PALETTE.border}`,
        borderRadius: 16,
        padding: '16px 20px',
        textAlign: 'left',
        transition: 'border-color 0.15s',
        cursor: 'pointer',
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 500, color: PALETTE.text, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: 12.5, color: PALETTE.textMid, fontWeight: 300, lineHeight: 1.5, marginBottom: 6 }}>
        {detail}
      </div>
      <div style={{ fontSize: 12, color: PALETTE.primary, fontWeight: 500, letterSpacing: '0.02em' }}>
        {action} →
      </div>
    </a>
  );
}
